<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader && !showFilters"
	:noWhiteBox="!getAccountMovements.length && !showFilters"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
	:showFiltersAndEmptyState="showFilters && !getAccountMovements.length && !applyErrorState"
    :showFiltersAndErrorState="showFilters && applyErrorState"
    :applyErrorState="applyErrorState"
	:showFloatingButton="showFilters && !applyErrorState"
    :showTopRightAreaSlot="renderAccountSelector"
    :floatingButtonParams="{ icon: 'filter', label: t('filters.filter')}"
    @floatingButtonClicked="showFiltersModal = true"
    @backOrCloseClicked="navigateBack ? navigateBack() : emit('backOrCloseClicked')">
        <!-- *********************************************
        *                 Account Selector               *
        ********************************************** -->
        <template #accounts-card-slot-area>
            <account-selector
            v-if="renderAccountSelector"
            :class="{ 'mobile-margin': getMobileAppState }"
            :selectedOption="selectedAccount.id"
            :options="getAccountsBalances"
            @clicked="selectAccountHandler"/>
        </template>
        <!-- *********************************************
        *          Download Excel Button Mobile          *
        ********************************************** -->
        <template v-slot:top-right-button-mobile-slot>
			<dropdown-more-options
			variant="tertiary"
			icon="download"
			class="top-right-mobile-button"
			:options="downloadMoreOptions"
			:class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }"
			@clicked="downloadMoreOptionsClickHandler"/>
        </template>
        <!-- *********************************************
        *                  Visible Filters               *
        ********************************************** -->
		<consult-filters-wrapper
		v-if="showFilters"
		:filterTags="filterTags"
		:applyButtonDisabled="applyButtonDisabled"
		@applyFilters="applyFilters(appliedFilters)"
		@removeFilterTag="removeFilter"
		@closeAllFilters="closeAllFilters">
			<!-- Double Calendar -->
			<input-double-calendar
			calendarDirection="row"
			:fromDateParams="beginDateParams"
			:toDateParams="endDateParams"
			@inputFromDate="appliedFilters.beginDate = $event"
			@inputToDate="appliedFilters.endDate = $event"/>

			<template v-if="!!getAccountMovements.length && !getMobileAppState" #extra-elements-to-right>
				<!-- Download Movements List Buttons -->
				<div class="buttons-wrapper">
					<button-secondary
					icon="download"
					buttonSize="small"
					:label="t('accounts.balancesAndMovements.excel')"
					@clicked="downloadExcelHandler"/>
					<button-secondary
					icon="download"
					buttonSize="small"
					:label="t('accounts.balancesAndMovements.pdf')"
					@clicked="downloadPdfHandler"/>
				</div>
			</template>
		</consult-filters-wrapper>
		<!-- *********************************************
		*												 *
		*                       Table                    *
		*												 *
		********************************************** -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState && !!getAccountMovements.length" class="content-wrapper prevent-auto-margins">
                <!-- Table Generic -->
				<table-group-by-date
				:headerData="movementsTableHeader"
				:tableStyles="movementsTableStyles"
				:tableData="movementsTableData"
				:hasMore="getMovementsHasMore"
                :isLoadingMore="tableLoadingMore"
				@loadMore="loadMoreHandler"
				@rowClicked="movementsClickHandler($event)"
				@moreOptionsClicked="moreOptionsClickHandler"
                @downloadClicked="downloadClickedHandler"/>
            </div>
        </transition>
        <!-- *********************************************
        *                    Empty State                 *
        ********************************************** -->
		<template v-if="!showLoader && !applyErrorState && !getAccountMovements.length" #empty-state-slot>
            <empty-state-generic
            :illustration="filterTags.length ? 'emptyState/filters' : 'emptyState/no-movements'"
            :buttonLabel="t('transfers.beneficiaries.emptyState.createBeneficiary')"
            :title="t(`accounts.balancesAndMovements.emptyState.${filterTags.length ? 'titleFilters' : 'titleMovements'}`)"
            :description="t(`accounts.balancesAndMovements.emptyState.${filterTags.length ? 'descriptionFilters' : 'descriptionMovements'}`)"/>
        </template>
    </wrapper-features-templates-one-column>
	<!-- Filters Modal -->
	<filters-modal
    v-if="showFiltersModal"
    :appliedFilters="appliedFilters"
	:beginDateParams="beginDateParams"
	:endDateParams="endDateParams"
	:accountCurrency="selectedAccount.currency"
    @hideMe="showFiltersModal = false"
    @applyFilters="applyFilters"/>
    <!-- Detail Modal -->
    <detail-modal
    v-if="showDetailsModal"
    :movementDetails="movementDetails"
    @hideMe="[movementDetails = null, showDetailsModal = false]"
    @showOverlayLoader="showOverlayLoader = $event"
    @requestListForCategoryUpdate="fetchMovements('updateCategory')"/>
    <!-- Send Info Modal -->
    <modal-centered-send-info
    v-if="showSendEmailInfoModal || showSendSMSInfoModal "
    emailAction="sendMovementEmailNotification"
    smsAction="sendMovementSMSNotification"
    :sendType="showSendEmailInfoModal ? 'email' : 'sms'"
    :payload="showSendInfoModalData"
    @hideMe="[showSendEmailInfoModal = false, showSendSMSInfoModal = false]"/>
    <!-- ShareLinkModal -->
    <modal-centered-share-link
    v-if="showShareLinkModal"
    :operationId="showSendInfoModalData.shareLinkOperationId"
    @hideMe="showShareLinkModal = false"/>
    <!-- Component to handle download logic-->
    <download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import AccountSelector from '@/components/generic/selectorsWithDetails/Accounts'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'
import TableGroupByDate from '@/components/generic/table/GroupByDate'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import FiltersModal from '@/components/features/accounts/balancesAndMovements/FiltersModal'
import ModalCenteredSendInfo from '@/components/generic/modals/centered/sendInfo/Base'
import DetailModal from '@/components/features/accounts/balancesAndMovements/MovementDetailModal'
import ModalCenteredShareLink from '@/components/generic/modals/centered/ShareLink'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* VUE & Assets & Helpers */
import { ref, computed, onBeforeUnmount } from 'vue'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { MOVEMENT_TRANSFER_TYPES } from '@/assets/constants/movements'
import { getDate, createJSONDateEndDay } from '@/assets/scripts/dateBuilders'
import { amountFormat } from '@/assets/scripts/amounts'
import { categoryIconMapper } from '@/assets/scripts/mappers/categories'
import { navigateBack } from '@/router/helpers/navigation'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { requestMovementDetailDownloadUrl, requestMovementsPdfUrl, createsDowloadMovementsExcelUrl } from '@/network/api/accounts.api.js'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* Props */
const props = defineProps({
	tempAccount: Number,
	navigateBack: Boolean
})

/* mapActions & mapGetters */
const { requestAccountsBalances, requestAccountsMovements, clearFeatureStates } = mapActions(moduleMapper.ACCOUNTS_BALANCES_AND_MOVEMENTS)
const { getAccountsBalances, getAccountMovements, getLastMovementInfo, getMovementsHasMore } = mapGetters(moduleMapper.ACCOUNTS_BALANCES_AND_MOVEMENTS)
const { getMobileAppState, getDeviceInUse } = mapGetters()

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const tableLoadingMore = ref(false)
const applyErrorState = ref(false)
const renderAccountSelector = ref(false)
const selectedAccount = ref(null)
const showSendEmailInfoModal = ref(false)
const showSendSMSInfoModal = ref(false)
const showSendInfoModalData = ref(null)
const showShareLinkModal = ref(false)
const showDetailsModal = ref(false)
const movementDetails = ref(null)
const showFilters = ref(false)
const accountFromPatrimony = ref(null)
/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const fetchAccountsBalance = async () => {
	renderAccountSelector.value = false
	showLoader.value = true
	applyErrorState.value = false
	showFeedbackToast.value = false
	showFilters.value = false

	try {
        await requestAccountsBalances()
        /* Setting Initial Selected Account */
        if (getAccountsBalances.value.length) {
            accountFromPatrimony.value = getAccountsBalances.value.find(account => account.id === props.tempAccount)

            selectedAccount.value = props.tempAccount ? accountFromPatrimony.value : getAccountsBalances.value.find(account => { return account.default })

            renderAccountSelector.value = true
            fetchMovements()

			if (getAccountMovements.value.length) showFilters.value = true
        } else {
            showLoader.value = false
        }
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showLoader.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, fetchAccountsBalance)
	}
}

const fetchMovements = async (updateCategory) => {
	if (updateCategory) showOverlayLoader.value = true
	else paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true

	applyErrorState.value = false
	showFeedbackToast.value = false
	cancelAllPendingRequests()

	const movementsPayload = {
		accountId: selectedAccount.value.id,
        params: {
			...paginationParams.value,
			...appliedFilters.value,
			endDate: createJSONDateEndDay(appliedFilters.value.endDate)
		}
	}

	try {
		await requestAccountsMovements(movementsPayload)
		if (getAccountMovements.value.length) showFilters.value = true
		if (updateCategory) {
            movementsClickHandler({ rowId: `${movementDetails.value.id}${movementDetails.value.orderNumberPosition}${movementDetails.value.releaseDatePosition}` })
            showOverlayLoader.value = false
        } else {
			showLoader.value = false
			tableLoadingMore.value = false
		}
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showLoader.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, fetchMovements)
	}
}

/*************************************************
*                                                *
*                Pagination Params               *
*                                                *
*************************************************/
const INITIAL_PAGINATION_PARAMS = {
    pageNumber: 1,
	pageSize: 20,
    orderNumberPosition: '',
    releaseDatePosition: ''
}

const paginationParams = ref({ ...INITIAL_PAGINATION_PARAMS })

const resetPaginationParams = () => {
    paginationParams.value = { ...INITIAL_PAGINATION_PARAMS }
}

const loadMoreHandler = () => {
    paginationParams.value.pageNumber++
    paginationParams.value.orderNumberPosition = getLastMovementInfo.value.orderNumberPosition
    paginationParams.value.releaseDatePosition = getLastMovementInfo.value.releaseDatePosition
    fetchMovements()
}
/*************************************************
*                                                *
*                   Filters                      *
*                                                *
*************************************************/
const movementTransfersTypes = MOVEMENT_TRANSFER_TYPES()
const showFiltersModal = ref(false)
const filterTags = ref([])

const appliedFilters = ref({
    movementTransferType: '',
    beginDate: '',
    endDate: '',
    minValue: '',
    maxValue: ''
})

/* Begin Date Params */
const beginDateParams = computed(() => {
    return {
        labelText: t('accounts.balancesAndMovements.beginDate'),
        placeholder: t('accounts.balancesAndMovements.beginDatePlaceholder'),
        inputValue: appliedFilters.value.beginDate,
        disabledDates: { from: new Date() }
    }
})

/* End Date Params */
const endDateParams = computed(() => {
    return {
        labelText: t('accounts.balancesAndMovements.endDate'),
        placeholder: t('accounts.balancesAndMovements.endDatePlaceholder'),
        inputValue: appliedFilters.value.endDate,
        disabledDates: { from: new Date() }
    }
})

const applyButtonDisabled = computed(() => {
    const filters = Object.values(appliedFilters.value)
    return filters.every(filter => !filter)
})

const applyFilters = (filters) => {
	filterTags.value = []

	for (const filter in filters) {
		if (filters[filter]) {
			appliedFilters.value[filter] = filters[filter]

			if (filterTags.value.find(tag => { return tag.id === filter })) {
				filterTags.value.find(tag => { return tag.id === filter }).text = filterTagTextHandler(filter, filters[filter])
			} else {
				filterTags.value.push({
					id: filter,
					label: t(`accounts.balancesAndMovements.${filter}`),
					text: filterTagTextHandler(filter, filters[filter])
				})
			}
		} else {
			appliedFilters.value[filter] = ''
		}
	}

	resetPaginationParams()
	fetchMovements()
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'beginDate':
		case 'endDate':
			return getDate(value)
		case 'minValue':
		case 'maxValue':
			return amountFormat(value, selectedAccount.value.currency)
		case 'movementTransferType':
			return movementTransfersTypes.find(type => type.id === value).text
		default: return value
	}
}

const removeFilter = (tagId) => {
	appliedFilters.value[tagId] = ''

	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []

    resetPaginationParams()
    fetchMovements()
}

const closeAllFilters = () => {
	filterTags.value = []

	for (const filter in appliedFilters.value) {
		appliedFilters.value[filter] = ''
    }

    resetPaginationParams()
	fetchMovements()
}

const selectAccountHandler = event => {
	selectedAccount.value = getAccountsBalances.value.find(account => account.id === event)
	showFilters.value = false
	closeAllFilters()
}

/*************************************************
*                                                *
*            Buttons Downloads Handler           *
*                                                *
*************************************************/
const downloadMoreOptions = [
	{
		id: 1,
		text: t('accounts.balancesAndMovements.pdf'),
		icon: 'download',
		callbackFunction: () => { downloadPdfHandler() }
	}, {
		id: 2,
		text: t('accounts.balancesAndMovements.excel'),
		icon: 'download',
		callbackFunction: () => { downloadExcelHandler() }
	}
]

const downloadMoreOptionsClickHandler = $event => {
	downloadMoreOptions.find(obj => obj.id === $event).callbackFunction()
}

const downloadPdfHandler = () => {
    const payload = {
        accountId: selectedAccount.value.id,
        params: {
			movementTransferType: appliedFilters.value.movementTransferType,
			beginDate: appliedFilters.value.beginDate,
			endDate: createJSONDateEndDay(appliedFilters.value.endDate),
			minValue: appliedFilters.value.minValue,
			maxValue: appliedFilters.value.maxValue
		}
    }

    downloadLink.value = requestMovementsPdfUrl(payload)
}

const downloadExcelHandler = () => {
    const payload = {
        accountId: selectedAccount.value.id,
        params: {
			movementTransferType: appliedFilters.value.movementTransferType,
			beginDate: appliedFilters.value.beginDate,
			endDate: createJSONDateEndDay(appliedFilters.value.endDate),
			minValue: appliedFilters.value.minValue,
			maxValue: appliedFilters.value.maxValue
		}
    }

    downloadLink.value = createsDowloadMovementsExcelUrl(payload)
}

/*************************************************
*                                                *
*              Movements Table Data              *
*                                                *
*************************************************/
const tableMoreOptions = () => {
    const moreOptions = [
        {
            id: 1,
            text: t('accounts.balancesAndMovements.moreOptions.sendEmail'),
            icon: 'letter',
            callbackFunction: () => { showSendEmailInfoModal.value = true }
        }, {
            id: 2,
            text: t('accounts.balancesAndMovements.moreOptions.sendSms'),
            icon: 'chat',
            callbackFunction: () => { showSendSMSInfoModal.value = true }
        }, {
            id: 3,
            text: t('accounts.balancesAndMovements.moreOptions.generateLink'),
            icon: 'link',
            callbackFunction: () => { showShareLinkModal.value = true }
        }
    ]

    if (getMobileAppState.value) {
        moreOptions.push({
            id: 4,
            text: t('accounts.balancesAndMovements.moreOptions.download'),
            icon: 'download',
            callbackFunction: (rowId) => { downloadClickedHandler(rowId) }
        })
    }
    return moreOptions
}

const movementsTableHeader = [
	{ label: '', showInMobile: true, dataAttr: 'category', rowCellType: 'category' },
	{ label: t('accounts.balancesAndMovements.tableHeader.description'), dataAttr: 'description', showInMobile: { groupWith: 'amount' }, rowCellType: 'title' },
    { label: t('accounts.balancesAndMovements.tableHeader.date'), dataAttr: 'amount', rowCellType: 'amount' },
	{ label: t('accounts.balancesAndMovements.tableHeader.amount'), dataAttr: 'amount', rowCellType: 'amount' },
	{ label: t('accounts.balancesAndMovements.tableHeader.accountBalance'), dataAttr: 'amountAfterMovement', rowCellType: 'amount' },
    { dataAttr: 'download', rowCellType: 'download' },
	{ dataAttr: 'tableOptions', showInMobile: true, rowCellType: 'more-options' }
]

const movementsTableStyles = [null, null, null, { 'text-align': 'end' }, { 'text-align': 'end' }, null, null]

const movementsTableData = computed(() => {
	return getAccountMovements.value.map(movement => {
		return {
			rowDate: movement.transactionDate,
			rowData: [
				{ value: `${movement.id}${movement.orderNumberPosition}${movement.releaseDatePosition}`, dataAttr: 'id' },
				{
					type: 'category',
					value: {
						backgroundColor: movement.categoryView ? movement.categoryView.color : null,
						icon: movement.categoryView ? categoryIconMapper(movement.categoryView.name) : 'brokenImage'
					},
					dataAttr: 'category'
				},
				{ type: 'title', value: movement.description, dataAttr: 'description' },
				{ value: { content: getDate(movement.transactionDate), classes: 'capitalize' }, dataAttr: 'date' },
				{
					type: 'amount',
					value: { content: movement.amount, currency: movement.currency, noBalanceIcon: false, hasArrowIcons: true },
					dataAttr: 'amount'
				},
				{
					type: 'amount',
					value: { content: movement.balance, currency: movement.currency, noBalanceIcon: false },
					dataAttr: 'amountAfterMovement'
				},
                { type: 'download', dataAttr: 'download' },
				{
					type: 'more-options',
					value: movement.sourceSystemView ? tableMoreOptions() : tableMoreOptions().filter(obj => obj.id !== 3),
					dataAttr: 'tableOptions',
					hidden: false
				}
			]
		}
	})
})

/*************************************************
*                                                *
*              Table Clicks Handlers             *
*                                                *
*************************************************/
const sendInfoDownloadData = rowId => {
    const movementDetails = getAccountMovements.value.find(movement => `${movement.id}${movement.orderNumberPosition}${movement.releaseDatePosition}` === rowId)

    return {
		operationId: movementDetails.id,
		movementId: movementDetails.id,
		accountId: selectedAccount.value.id,
		params: {
			releaseDatePosition: movementDetails.releaseDatePosition,
			orderNumberPosition: movementDetails.orderNumberPosition
		},
		shareLinkOperationId: movementDetails.sourceSystemView ? movementDetails.sourceSystemView.operationId : null
	}
}

const moreOptionsClickHandler = row => {
	showSendInfoModalData.value = sendInfoDownloadData(row.rowId)
	tableMoreOptions().find(obj => obj.id === row.optionClicked).callbackFunction(row.rowId)
}

const downloadLink = ref(null)
const downloadClickedHandler = rowId => {
    downloadLink.value = requestMovementDetailDownloadUrl(sendInfoDownloadData(rowId))
}

const movementsClickHandler = row => {
	const movement = getAccountMovements.value.find(movement => `${movement.id}${movement.orderNumberPosition}${movement.releaseDatePosition}` === row.rowId)

	movementDetails.value = {
		...movement,
		accountId: selectedAccount.value.id,
		accountType: selectedAccount.value.type
	}
	showDetailsModal.value = true
}

/*************************************************
*                                                *
*                 Error Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

const showPdfViewer = ref(false)
// 'https://app.inm.pt/BAI-MDW-DEV-3G/internet-banking/api/internet/accounts/2471/movements/321471154/receipt?releaseDatePosition=2024-08-02T11:00:00.000Z&orderNumberPosition=-9959408&noCacheHelper=1722854678411&token=072c1656-5670-4320-bbd3-f57647a88db4.%252BDZyO6WvLbEaipqoGRak3BQ3ynmqTetfSYpmHqeFt8Y%253D&appVersion=v1.06.02'
const teste = computed(() => {
	return {
		accountId: selectedAccount.value.id,
		params: {
			movementTransferType: appliedFilters.value.movementTransferType,
			beginDate: appliedFilters.value.beginDate,
			endDate: createJSONDateEndDay(appliedFilters.value.endDate),
			minValue: appliedFilters.value.minValue,
			maxValue: appliedFilters.value.maxValue
		}
	}
})

fetchAccountsBalance()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.top-right-mobile-button.dropdown-wrapper {
    position: absolute;
    top: 16px;
    right: 16px;

    z-index: 3;

    &.mobile-status-bar-android {
        top: calc(16px + var(--android-top-padding));
    }

    &.mobile-status-bar-ios {
        top: calc(16px + constant(safe-area-inset-top));
        top: calc(16px + env(safe-area-inset-top));
    }
}

.buttons-wrapper {
	width: fit-content;
	height: 100%;

	display: flex;
	align-items: center;
	gap: 16px;
}
</style>
